import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { timestampJsonReviver } from "cloud-core/utilities/json";
import { ApplicationState } from "../";
import { Organisation } from "../../models/Organisation";
import { Permission, ResourcePermissionRequest, Role, User, UserGroup } from "../../models/User";
import { UserConfig } from "../../models/UserConfig";
import { Processor, ProcessorTypes } from "../../models/admin/Processor";
import { AnalyticsDefinition, AnalyticsMetadata } from "../../models/analysis/Config";
import { Run } from "../../models/analysis/Runs";
import { IntegrationProject } from "../../models/integrations/IntegrationProject";
import { AnalysisRequest } from "../../models/media/AnalysisRequest";
import { FetchedFragmentGroup } from "../../models/media/FragmentGroup";
import {
    AddMediaSourceRequestBody,
    IntegrationMediaSource,
    MediaSource,
    sortMediaSources,
} from "../../models/media/MediaSource";
import { addAuthHeaders } from "../../utilities/Auth";
import { actionCreators } from "../user/User";
import { StoredFile } from "../../models/media/StoredFile";
import { FragmentsMap } from "../../views/playback/components/useLoadFragments";
import { MediaSourceWithExtras } from "../media/MediaItems";
import { Project } from "../../models/Project";
import { TagObject } from "../../models/tags/TagObject";
import { TagEvent } from "../../models/tags/TagEvent";
import { fetchAllPages } from "./utils";
import VideoClip, { VideoClipRequest } from "../../models/media/VideoClip";
import { Task, TaskLogEntry } from "../../models/tasks/Task";
import { AuditLogEntry } from "../../models/audit/AuditLogEntry";
import { FilterSet } from "cloud-core/filters/FilterSet";

export interface BaseAdminQueryArgs {
    organisationId: string;
}
export type BaseAugmentationArgs = { organisationId?: string };
export type AugmentationWithPayloadArgs = BaseAugmentationArgs & { payload: string };
export type AugmentUserArgs = BaseAugmentationArgs & { username: string };
export type ProjectArgs = { projectId: string };
export type MediaSourceArgs = { mediaId: string };
export type ProjectMediaSourceArgs = ProjectArgs & MediaSourceArgs;

export type FileData = {
    fileId: string;
    startsAt: number;
};

const tagTypes = [
    "userConfig",
    "projects",
    "tasks",
    "users",
    "organisation",
    "organisations",
    "media",
    "processors",
    "fragmentGroups",
    "fragments",
    "runs",
    "tagObjects",
    "tagEvents",
    "analyticsMetadata",
    "tagObjects",
    "tagEvents",
    "userGroups",
    "filters",
] as const;
type TagType = (typeof tagTypes)[number];
interface Tag {
    type: TagType;
    id: string;
}

const UI_API_VERSION = "0.1.0";
const usersTag: Tag = { type: "users", id: "0" };
const getUsersAdminTag = (args: BaseAugmentationArgs) => ({ type: "users", id: args.organisationId }) as Tag;

const baseQuery = fetchBaseQuery({
    baseUrl: `https://${process.env.REACT_APP_KINESENSE_API_BASE}`,
    prepareHeaders: (headers, { getState }) => {
        const state = getState() as ApplicationState;

        if (state && state.user !== undefined) {
            addAuthHeaders(state.user.authToken, headers);
        }

        return headers;
    },
});

export interface KinesenseApiResponse<T> {
    data: T;
    hasNextPage: boolean;
    pageKey: string | undefined;
    error: FetchBaseQueryError;
}

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    { usePaging?: boolean } | undefined
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
        api.dispatch(actionCreators.refreshAuthSession());

        result = await baseQuery(args, api, extraOptions);

        if (result.error?.status === 401) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            result = await baseQuery(args, api, extraOptions);
        }
    }

    if (result.error || !result.data) {
        return result;
    }

    if (extraOptions?.usePaging) {
        return result;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return result.data as any;
};

function createQueryString(params: Record<string, string | number | boolean | undefined>) {
    const esc = encodeURIComponent;
    const query = Object.entries(params)
        .map(([key, value]) => {
            if (value === undefined) {
                return "";
            }

            return `${esc(key)}=${esc(String(value))}`;
        })
        .filter(Boolean)
        .join("&");
    return query ? `?${query}` : "";
}

export const kinesenseApiSlice = createApi({
    reducerPath: "api",
    baseQuery: baseQueryWithReauth,
    tagTypes: tagTypes,
    endpoints: (builder) => ({
        // PROJECTS
        getProjects: builder.query<Project[], void>({
            async queryFn(_args, _queryApi, _extraOptions, fetchWithBQ) {
                const { data, error } = await fetchAllPages<Project[]>(fetchWithBQ, "/projects");
                if (error !== undefined) {
                    return { error };
                }
                return {
                    data: data
                        // Sort by `modifiedAt`, latest projects should come first
                        .sort((a, b) => b.modifiedAt - a.modifiedAt),
                };
            },
            providesTags: ["projects"],
        }),
        getProject: builder.query<Project, ProjectArgs>({
            query: (args) => `/projects/${args.projectId}`,
        }),
        getProjectThumbnails: builder.query<
            { thumbnails: Record<string, string>; expiresAt: number },
            ProjectArgs & { pageKey?: string }
        >({
            query: (args) =>
                `/projects/${args.projectId}/media-thumbnails` + (args.pageKey ? `?pageKey=${args.pageKey}` : ""),
        }),
        addProject: builder.mutation<Project, { project: Partial<Project>; permissions: ResourcePermissionRequest }>({
            query: (args) => ({
                url: "/projects",
                method: "POST",
                body: args,
            }),
            invalidatesTags: ["projects"],
        }),
        // TASKS
        getTasks: builder.query<Task[], ProjectArgs>({
            async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
                const { data, error } = await fetchAllPages<Task[]>(fetchWithBQ, `/projects/${args.projectId}/tasks`);
                if (error !== undefined) {
                    return { error };
                }

                const now = new Date().getTime();
                const limit_ms = 2 * 24 * 60 * 60 * 1000;

                return {
                    data: data
                        // Only show recent tasks
                        .filter((t) => t.startedAt >= now - limit_ms)
                        // Sort by `startedAt`, latest tasks should come first
                        .sort((a, b) => b.startedAt - a.startedAt),
                };
            },
            providesTags: ["tasks"],
        }),
        getTask: builder.query<Project, ProjectArgs & { taskId: string }>({
            query: (args) => `/projects/${args.projectId}/tasks/${args.taskId}`,
        }),
        // AUDIT LOG
        getAuditLogEntries: builder.query<
            KinesenseApiResponse<AuditLogEntry[]>,
            { typePrefix?: string; pageKey?: string; limit?: number }
        >({
            extraOptions: { usePaging: true },
            query: (args) =>
                `/audit/entries` +
                createQueryString({
                    type: args.typePrefix,
                    pageKey: args.pageKey,
                    limit: args.limit,
                }),
        }),
        getLastOrgActivity: builder.query<KinesenseApiResponse<{ lastActivity: number }>, { organisationId: string }>({
            extraOptions: { usePaging: true },
            query: (args) => `/admin/orgs/${args.organisationId}/audit/get-last-org-activity`,
        }),
        getTaskLogs: builder.query<
            KinesenseApiResponse<TaskLogEntry[]>,
            ProjectArgs & { taskId: string; pageKey?: string }
        >({
            extraOptions: { usePaging: true },
            query: (args) =>
                `/projects/${args.projectId}/tasks/${args.taskId}/logs` + createQueryString({ pageKey: args.pageKey }),
        }),

        // USERS
        getCurrentUserConfig: builder.query<UserConfig, void>({
            query: () => "/user/config/general",
            providesTags: ["userConfig"],
        }),
        updateCurrentUserConfig: builder.mutation<void, Partial<UserConfig>>({
            query: (args) => ({ url: "/user/config/general", method: "PATCH", body: JSON.stringify(args) }),
            invalidatesTags: ["userConfig"],
        }),
        getUser: builder.query<User, { userId: string }>({
            query: (args) => `/org/users/${args.userId}`,
            transformResponse: (user: Partial<User>) => new User(user),
        }),
        getUsers: builder.query<User[], { pageKey?: string }>({
            query: (args) => {
                return "/org/users" + (args?.pageKey ? `?pageKey=${args.pageKey}` : "");
            },
            transformResponse: (users: Partial<User>[]) => users.map((user) => new User(user)),
            providesTags: [usersTag],
        }),
        getUserPermissions: builder.query<Permission[], { username: string }>({
            query: (args) => `/org/users/${args.username}/permissions`,
        }),
        adminGetUsers: builder.query<User[], BaseAdminQueryArgs & { pageKey?: string }>({
            query: (args) =>
                `/admin/orgs/${args.organisationId}/users` + (args.pageKey ? `?pageKey=${args.pageKey}` : ""),
            transformResponse: (users: Partial<User>[]) => users.map((user) => new User(user)),
            providesTags: (_, __, admin_query_args) => [getUsersAdminTag(admin_query_args)],
        }),

        addUser: builder.mutation<unknown, AugmentationWithPayloadArgs>({
            query: (args) => ({
                url: "/org/users",
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: [usersTag],
        }),
        adminAddUser: builder.mutation<unknown, AugmentationWithPayloadArgs>({
            query: (args) => ({
                url: `/admin/orgs/${args.organisationId}/users`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: (_, __, augmentation_args) => [getUsersAdminTag(augmentation_args)],
        }),

        deleteUser: builder.mutation<void, AugmentUserArgs>({
            query: (args) => ({
                url: `/org/users/${args.username}`,
                method: "DELETE",
            }),
            invalidatesTags: [usersTag],
        }),
        adminDeleteUser: builder.mutation<void, AugmentUserArgs>({
            query: (args) => ({
                url: `/admin/orgs/${args.organisationId}/users/${args.username}`,
                method: "DELETE",
            }),
            invalidatesTags: (_, __, augment_user_args) => [getUsersAdminTag(augment_user_args)],
        }),

        resetUserPassword: builder.mutation<void, AugmentUserArgs>({
            query: (args) => ({
                url: `/org/users/${args.username}/reset-password`,
                method: "POST",
            }),
            invalidatesTags: [usersTag],
        }),
        adminResetUserPassword: builder.mutation<void, AugmentUserArgs>({
            query: (args) => ({
                url: `/admin/orgs/${args.organisationId}/users/${args.username}/reset-password`,
                method: "POST",
            }),
            invalidatesTags: (_, __, augment_user_args) => [getUsersAdminTag(augment_user_args)],
        }),

        adminSetUserRole: builder.mutation<
            { username: string; groupIds: string[] },
            AugmentUserArgs & { body: { role: Role } }
        >({
            query: (args) => ({
                url: `/admin/orgs/${args.organisationId}/users/${args.username}/role`,
                method: "PUT",
                body: args.body,
            }),
            invalidatesTags: (_, __, augment_user_args) => [getUsersAdminTag(augment_user_args)],
        }),

        // USER GROUPS
        getUserGroups: builder.query<UserGroup[], void>({
            query: (args) => `/org/groups`,
            transformResponse: (fetchedGroups: UserGroup[]) => {
                return fetchedGroups.map((group) => ({
                    ...group,
                    isAdmin: group.groupId == Organisation.createOrganisationAdminGroupId(),
                    isManager: group.groupId == Organisation.createOrganisationManagerGroupId(group.organisationId),
                    isBasic: group.groupId == Organisation.createOrganisationBasicUserGroupId(group.organisationId),
                }));
            },
            providesTags: ["userGroups"],
        }),
        getUserGroupPermissions: builder.query<Permission[], { groupId: string }>({
            query: (args) => `/org/groups/${args.groupId}/permissions`,
        }),
        addUserGroup: builder.mutation<void, { name: string; description?: string; usernames: string[] }>({
            query: (newGroup) => ({
                url: `/org/groups`,
                method: "POST",
                body: JSON.stringify(newGroup),
            }),
            invalidatesTags: ["userGroups"],
        }),
        addUserToGroup: builder.mutation<void, { groupId: string; username: string }>({
            query: (args) => ({
                url: `/org/groups/${args.groupId}/users/${args.username}`,
                method: "POST",
            }),
            invalidatesTags: ["userGroups", "users"],
        }),
        removeUserFromGroup: builder.mutation<void, { groupId: string; username: string }>({
            query: (args) => ({
                url: `/org/groups/${args.groupId}/users/${args.username}`,
                method: "DELETE",
            }),
            invalidatesTags: ["userGroups", "users"],
        }),

        // ORGANISATIONS
        getOrganisation: builder.query<Organisation, void>({
            query: () => "/org",
            providesTags: ["organisation"],
        }),

        getOrganisationGeneralSettings: builder.query<
            { integrations: string[]; enabledAnalysers: string[]; isTrial: boolean },
            void
        >({
            query: () => "/org/config/general",
        }),

        adminGetOrganisations: builder.query<Organisation[], { pageKey?: string }>({
            query: (args) => "/admin/orgs" + (args.pageKey ? `?pageKey=${args.pageKey}` : ""),
            providesTags: ["organisations"],
        }),

        adminAddOrganisation: builder.mutation<Organisation, AugmentationWithPayloadArgs>({
            query: (args) => ({
                url: "/admin/orgs",
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: ["organisations"],
        }),

        // MEDIA
        getAllMedia: builder.query<MediaSource[], ProjectArgs>({
            async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
                const { data, error } = await fetchAllPages<MediaSource[]>(
                    fetchWithBQ,
                    `/projects/${args.projectId}/media`,
                );
                if (error !== undefined) {
                    return { error };
                }

                data.sort(sortMediaSources);

                return { data };
            },
            providesTags: ["media"],
        }),
        addMediaSource: builder.mutation<MediaSource, AddMediaSourceRequestBody>({
            query: (mediaSource) => ({
                url: `/projects/${mediaSource.projectId}/media`,
                method: "POST",
                body: mediaSource,
                responseHandler: (response) =>
                    response
                        .text()
                        .then((txt) => JSON.parse(txt, timestampJsonReviver(["startsAt", "createdAt", "updatedAt"]))),
            }),
            invalidatesTags: ["media"],
        }),
        getMediaSource: builder.query<MediaSourceWithExtras, ProjectMediaSourceArgs>({
            query: (args) => `/projects/${args.projectId}/media/${args.mediaId}`,
            transformResponse: (fetchedMediaSource: MediaSourceWithExtras) => {
                fetchedMediaSource.endsAt = fetchedMediaSource.startsAt + fetchedMediaSource.duration;
                return fetchedMediaSource;
            },
        }),

        getStoredFile: builder.query<StoredFile, { fileId: string }>({
            query: (args) => `/files/${args.fileId}`,
        }),

        getStoredFileForMedia: builder.query<StoredFile, { projectId: string; mediaId: string; fileId: string }>({
            query: (args) => `/projects/${args.projectId}/media/${args.mediaId}/files/${args.fileId}`,
        }),

        getStoredFilesForMedia: builder.query<Map<string, StoredFile>, { projectId: string; mediaId: string }>({
            query: (args) => `/projects/${args.projectId}/media/${args.mediaId}/files`,
            transformResponse: (fetchedFiles: StoredFile[]) => {
                const filesMap = new Map<string, StoredFile>();
                for (const file of fetchedFiles) {
                    filesMap.set(file.fileId, file);
                }
                return filesMap;
            },
        }),

        getStoredFilesByGroupPrefix: builder.query<Map<string, StoredFile>, { groupIdPrefix: string }>({
            query: (args) => `/files?groupIdPrefix=${args.groupIdPrefix}`,
            transformResponse: (fetchedFiles: StoredFile[]) => {
                const filesMap = new Map<string, StoredFile>();
                for (const file of fetchedFiles) {
                    filesMap.set(file.fileId, file);
                }
                return filesMap;
            },
        }),

        getVideoClips: builder.query<VideoClip[], ProjectMediaSourceArgs>({
            async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
                const { data, error } = await fetchAllPages<VideoClip[]>(
                    fetchWithBQ,
                    `/projects/${args.projectId}/media/${args.mediaId}/clips`,
                );
                if (error !== undefined) {
                    return { error };
                }

                // Ensure clips are sorted by their creation time
                data.sort((a, b) => b.createdAt - a.createdAt);

                return { data };
            },
        }),
        addVideoClip: builder.mutation<
            {
                fileId: string;
                jobId: string;
            },
            ProjectMediaSourceArgs & { payload: VideoClipRequest }
        >({
            query: (args) => ({
                url: `/projects/${args.projectId}/media/${args.mediaId}/clips`,
                method: "POST",
                body: args.payload,
            }),
        }),

        // FILTERS
        getFilter: builder.query<FilterSet, ProjectArgs & { filterId: string }>({
            query: (args) => `/projects/${args.projectId}/filters/${args.filterId}`,
            providesTags: ["filters"],
        }),
        getFiltersForProject: builder.query<FilterSet[], ProjectArgs>({
            query: (args) => `/projects/${args.projectId}/filters`,
            providesTags: ["filters"],
        }),
        addFilter: builder.mutation<void, ProjectArgs & { payload: string }>({
            query: (args) => ({
                url: `/projects/${args.projectId}/filters`,
                method: "POST",
                body: args.payload,
            }),
            invalidatesTags: ["filters"],
        }),
        updateFilter: builder.mutation<void, ProjectArgs & { filterId: string; payload: string }>({
            query: (args) => ({
                url: `/projects/${args.projectId}/filters/${args.filterId}`,
                method: "PUT",
                body: args.payload,
            }),
            invalidatesTags: ["filters"],
        }),
        deleteFilter: builder.mutation<void, ProjectArgs & { filterId: string }>({
            query: (args) => ({
                url: `/projects/${args.projectId}/filters/${args.filterId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["filters"],
        }),

        // INTEGRATIONS
        getAllIntegrationMedia: builder.query<IntegrationMediaSource[], { integrationId: string }>({
            query: (args) => `/integrations/${args.integrationId}/media`,
        }),
        requestIntegrationTransfer: builder.mutation<
            void,
            MediaSourceArgs & { integrationId: string; integrationMediaId: string }
        >({
            query: (args) => ({
                url: `/integrations/${args.integrationId}/start-transfer`,
                method: "POST",
                body: {
                    integrationMediaId: args.integrationMediaId,
                    linkedObjectId: args.mediaId,
                    linkedObjectType: "mediasource",
                },
            }),
        }),
        getAllIntegrationProjects: builder.query<IntegrationProject[], { integrationId: string }>({
            query: (args) => `/integrations/${args.integrationId}/projects`,
        }),
        exportMediaToIntegration: builder.mutation<
            string,
            ProjectMediaSourceArgs & {
                integrationId: string;
                title: string;
                description: string;
                startsAt: number;
                endsAt: number;
                integrationProjectId: string;
                folderId: string;
            }
        >({
            query: (args) => ({
                url: `/projects/${args.projectId}/media/${args.mediaId}/export-clip`,
                method: "POST",
                responseHandler: (response) => response.text(),
                body: {
                    integrationId: args.integrationId,
                    title: args.title,
                    description: args.description,
                    startsAt: args.startsAt,
                    endsAt: args.endsAt,
                    projectId: args.integrationProjectId,
                    folderId: args.folderId,
                },
            }),
        }),

        // ANALYSIS
        getRuns: builder.query<Run[], MediaSourceArgs>({
            query: (args) => `/media/${args.mediaId}/analytics/runs`,
            providesTags: (_result, _error, args) => [{ type: "runs", id: args.mediaId }],
        }),
        getFragmentsFromGroups: builder.query<
            FragmentsMap,
            MediaSourceArgs & { runId: number; groupIds: [string, ...string[]] }
        >({
            async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
                const { data, error } = await fetchAllPages<FragmentsMap>(
                    fetchWithBQ,
                    `/media/${args.mediaId}/analytics/runs/${args.runId}/groups/fragments?groupId=${args.groupIds.join(
                        ",",
                    )}`,
                );
                if (error !== undefined) {
                    return { error };
                }

                // Sort each array of fragments by their frameTimeOffset
                for (const groupId in data) {
                    data[groupId].sort((a, b) => a.frameTimeOffset - b.frameTimeOffset);
                }

                return { data };
            },
            providesTags: (_result, _error, args) => [
                { type: "fragments", id: `${[...args.groupIds].sort((a, b) => a.localeCompare(b)).join(",")}` },
            ],
        }),
        getFragmentGroups: builder.query<FetchedFragmentGroup[], MediaSourceArgs & { runId: number }>({
            async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
                const { data, error } = await fetchAllPages<FetchedFragmentGroup[]>(
                    fetchWithBQ,
                    `/media/${args.mediaId}/analytics/runs/${args.runId}/groups`,
                );
                if (error !== undefined) {
                    return { error };
                }

                // Ensure groups are sorted by starting offset
                data.sort((a, b) => a.startOffset - b.startOffset);

                return { data };
            },
            providesTags: (_result, _error, args) => [{ type: "fragmentGroups", id: `${args.mediaId}-${args.runId}` }],
            keepUnusedDataFor: 180,
        }),
        getAccessCookies: builder.query<
            Record<string, string>,
            MediaSourceArgs & { target: "frag-image" | "group-clip" }
        >({
            query: (args) => ({
                url: `/media/${args.mediaId}/get-access-cookies?target=${args.target}`,
                method: "GET",
                credentials: "include",
            }),
        }),
        getAnalysersConfig: builder.query<AnalyticsDefinition, void>({
            query: () => "/org/config/analysers",
        }),
        getAnalyticsMetadata: builder.query<AnalyticsMetadata, { pageKey?: string }>({
            query: (args) => "/org/config/aMetadata" + (args.pageKey ? `?pageKey=${args.pageKey}` : ""),
            providesTags: ["analyticsMetadata"],
        }),
        setAnalyticsMetadata: builder.mutation<void, AnalyticsMetadata>({
            query: (args) => ({ url: "/org/config/aMetadata", method: "PUT", body: JSON.stringify(args) }),
        }),
        deleteAnalyticsMetadataGroup: builder.mutation<void, { groupId: string }>({
            query: (args) => ({ url: `/org/config/aMetadata/${args.groupId}`, method: "DELETE" }),
        }),
        requestAnalysis: builder.mutation<Run, MediaSourceArgs & { analysisRequest: AnalysisRequest }>({
            query: (args) => ({
                url: `/media/${args.mediaId}/queue-analysis`,
                method: "POST",
                body: { ...args.analysisRequest, uiApiVersion: UI_API_VERSION },
            }),
        }),
        linkFilesToMediaSource: builder.mutation<void, ProjectMediaSourceArgs & { files: FileData[] }>({
            query: (args) => ({
                url: `/projects/${args.projectId}/media/${args.mediaId}/link-files`,
                method: "POST",
                body: JSON.stringify({ files: args.files, uiApiVersion: UI_API_VERSION }),
            }),
        }),

        // TAGS
        getAllTagObjects: builder.query<TagObject[], ProjectArgs>({
            async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
                const { data, error } = await fetchAllPages<TagObject[]>(
                    fetchWithBQ,
                    `/projects/${args.projectId}/tags/objects`,
                );
                if (error !== undefined) {
                    return { error };
                }
                return { data };
            },
            providesTags: ["tagObjects"],
        }),
        createTagObject: builder.mutation<TagObject, ProjectArgs & { tagObject: Partial<TagObject> }>({
            query: (args) => ({
                url: `/projects/${args.projectId}/tags/objects`,
                method: "POST",
                body: args.tagObject,
            }),
            invalidatesTags: ["tagObjects"],
        }),
        updateTagObject: builder.mutation<
            TagObject,
            ProjectArgs & { tagObjectId: string; tagObject: Partial<TagObject> }
        >({
            query: (args) => ({
                url: `/projects/${args.projectId}/tags/objects/${args.tagObjectId}`,
                method: "PATCH",
                body: args.tagObject,
            }),
            invalidatesTags: ["tagObjects"],
        }),
        getAllTagEvents: builder.query<TagEvent[], ProjectArgs>({
            async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
                // Get all possible tag objects, used for extending the tag events
                const { data: objects, error: objectsError } = await fetchAllPages<TagObject[]>(
                    fetchWithBQ,
                    `/projects/${args.projectId}/tags/objects`,
                );
                if (objectsError !== undefined) {
                    return { error: objectsError };
                }

                const { data, error } = await fetchAllPages<TagEvent[]>(
                    fetchWithBQ,
                    `/projects/${args.projectId}/tags/events`,
                );
                if (error !== undefined) {
                    return { error };
                }

                // Extend tag events with associated tag objects
                for (const event of data) {
                    event.subjects = [];
                    event.objects = [];
                    event.locations = [];
                    for (const object of objects) {
                        if (event.subjectIds.includes(object.objectId)) {
                            event.subjects.push(object);
                        } else if (event.objectIds.includes(object.objectId)) {
                            event.objects.push(object);
                        } else if (event.locationIds.includes(object.objectId)) {
                            event.locations.push(object);
                        }
                    }
                }

                return { data };
            },
            providesTags: ["tagEvents"],
        }),
        createTagEvent: builder.mutation<TagEvent, ProjectArgs & { tagEvent: Partial<TagEvent> }>({
            query: (args) => ({
                url: `/projects/${args.projectId}/tags/events`,
                method: "POST",
                body: args.tagEvent,
            }),
            invalidatesTags: ["tagEvents"],
        }),
        updateTagEvent: builder.mutation<TagEvent, ProjectArgs & { tagEventId: string; tagEvent: Partial<TagEvent> }>({
            query: (args) => ({
                url: `/projects/${args.projectId}/tags/events/${args.tagEventId}`,
                method: "PATCH",
                body: args.tagEvent,
            }),
            invalidatesTags: ["tagEvents"],
        }),

        // PROCESSORS
        adminGetProcessor: builder.query<Processor, { processorId: string }>({
            query: (args) => `/analysis/processors/${args.processorId}`,
        }),

        adminGetAllProcessors: builder.query<Processor[], void>({
            query: () => "/analysis/processors",
            providesTags: ["processors"],
        }),

        adminGetProcessorTypes: builder.query<ProcessorTypes, void>({
            query: () => "/analysis/processor-types",
        }),

        adminAddProcessor: builder.mutation<Processor, { type: string }>({
            query: (args) => ({
                url: "/analysis/processors",
                method: "POST",
                body: args,
            }),
            invalidatesTags: ["processors"],
        }),

        forceJobProcessing: builder.mutation<void, { jobId: string }>({
            query: (args) => ({
                url: `/jobs/${args.jobId}/process-job`,
                method: "POST",
            }),
        }),
    }),
});

export const {
    endpoints,
    useGetProjectsQuery,
    useGetProjectQuery,
    useGetProjectThumbnailsQuery,
    useGetTasksQuery,
    useGetTaskQuery,
    useGetTaskLogsQuery,
    useAddProjectMutation,
    useLazyGetCurrentUserConfigQuery,
    useGetCurrentUserConfigQuery,
    useGetUsersQuery,
    useGetUserGroupsQuery,
    useUpdateCurrentUserConfigMutation,
    useAdminGetUsersQuery,
    useAddUserMutation,
    useAdminAddUserMutation,
    useDeleteUserMutation,
    useAdminDeleteUserMutation,
    useResetUserPasswordMutation,
    useAdminResetUserPasswordMutation,
    useGetOrganisationQuery,
    useGetOrganisationGeneralSettingsQuery,
    useAdminGetOrganisationsQuery,
    useAdminAddOrganisationMutation,
    useGetAllMediaQuery,
    useLazyGetAllMediaQuery,
    useGetStoredFileQuery,
    useGetStoredFileForMediaQuery,
    useGetStoredFilesForMediaQuery,
    useGetStoredFilesByGroupPrefixQuery,
    useLazyGetStoredFileQuery,
    useAdminGetAllProcessorsQuery,
    useAdminGetProcessorQuery,
    useAdminGetProcessorTypesQuery,
    useAdminAddProcessorMutation,
    useAdminSetUserRoleMutation,
    useLazyGetFragmentGroupsQuery,
    useGetFragmentGroupsQuery,
    useGetAccessCookiesQuery,
    useLazyGetAccessCookiesQuery,
    useLazyGetFragmentsFromGroupsQuery,
    useGetAnalysersConfigQuery,
    useGetAnalyticsMetadataQuery,
    useSetAnalyticsMetadataMutation,
    useDeleteAnalyticsMetadataGroupMutation,
    useGetRunsQuery,
    useLazyGetRunsQuery,
    useAddMediaSourceMutation,
    useRequestAnalysisMutation,
    useGetAllIntegrationMediaQuery,
    useRequestIntegrationTransferMutation,
    useGetAllIntegrationProjectsQuery,
    useExportMediaToIntegrationMutation,
    useGetMediaSourceQuery,
    useLazyGetMediaSourceQuery,
    useGetAllTagObjectsQuery,
    useCreateTagObjectMutation,
    useUpdateTagObjectMutation,
    useGetAllTagEventsQuery,
    useCreateTagEventMutation,
    useUpdateTagEventMutation,
    useLinkFilesToMediaSourceMutation,
    useGetVideoClipsQuery,
    useLazyGetVideoClipsQuery,
    useAddVideoClipMutation,
    useAddUserGroupMutation,
    useGetUserGroupPermissionsQuery,
    useAddUserToGroupMutation,
    useRemoveUserFromGroupMutation,
    useForceJobProcessingMutation,
    useGetUserQuery,
    useGetUserPermissionsQuery,
    useGetAuditLogEntriesQuery,
    useGetLastOrgActivityQuery,
    useGetFiltersForProjectQuery,
    useAddFilterMutation,
    useUpdateFilterMutation,
    useDeleteFilterMutation,
} = kinesenseApiSlice;
